import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { GridPaginationModel } from '@mui/x-data-grid';

//import { FWorkbenchShell } from '../workbench';
import FPlanningJourney from '../core/FPlanningJourney';
import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';
//import { PageProps } from '../../../components/types/coretypes';

import HistoricalImportMasterDetail from './HistoricalImportMasterDetail';
import HistoricalOverviewPanel, { HistoricalGapResolutionMode } from './HistoricalOverviewPanel';
import HistoricalIntegrityPanel from './HistoricalIntegrityPanel';
import HistoricalManualEntryGrid from './HistoricalManualEntryGrid';
import HistoricalWorkbookPanel from './HistoricalWorkbookPanel';
import {
  downloadAllHistoricalGaps,
  downloadSelectedHistoricalGaps,
} from './biehistoricalworkbook.client';

import {
  commitMatrixResolutions,
  completeHistoricalPhase,
  fetchHistoricalFactsPage,
  fetchHistoricalGapsPage,
  fetchHistoricalImportsPage,
  fetchHistoricalIssuesPage,
  fetchHistoricalWorkbench,
  generateHistoricalMatrix,
  saveHistoricalFacts,
  setHistoricalGapStrategy,
  validateHistoricalCoverage,
} from './bieHistorical.apis';

import {
  BieHistoricalWorkbenchModel,
  HistoricalEntryDraft,
  HistoricalFactRow,
  HistoricalImportRow,
  HistoricalIntegrityIssue,
  HistoricalGapRow,
  HistoricalGapSummary,
  HistoricalIssueSeverity,
  MatrixResolutionCode,
  MatrixResolutionSaveRecord,
} from './bieHistorical.models';
import { PlanningContextPageProps, PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';

type HistoricalTab = 'overview' | 'imports' | 'manual' | 'workbook' | 'coverage' | 'complete';

type State = {
  tab: HistoricalTab;
  loading: boolean;
  loadingFacts: boolean;
  loadingIssues: boolean;
  error: string;
  success: string;
  phases: any[];
  workbench: BieHistoricalWorkbenchModel | null;
  selectedImport: HistoricalImportRow | null;
  facts: HistoricalFactRow[];
  metricCode: string;
  fiscalYear: string;
  manualRows: HistoricalEntryDraft[];
  completionNotes: string;
  imports: HistoricalImportRow[];
  importRowCount: number;
  importPaginationModel: GridPaginationModel;
  factRowCount: number;
  factPaginationModel: GridPaginationModel;
  issues: HistoricalIntegrityIssue[];
  issueRowCount: number;
  issuePaginationModel: GridPaginationModel;
  issueSeverity: HistoricalIssueSeverity | '';

  gaps: HistoricalGapRow[];
  gapSummary: HistoricalGapSummary;
  gapRowCount: number;
  gapPaginationModel: GridPaginationModel;
  selectedGapKeys: string[];
  loadingGaps: boolean;
  downloadingGaps: boolean;

  gapResolutionMode: HistoricalGapResolutionMode;
};

// interface PlanningPageProps extends PageProps, PlanningContextPageProps {
//   onBack?: () => void;
// }

class BieHistoricalWorkbench extends React.Component<PlanningPageProps, State> {
  state: State = {
    tab: 'overview',
    loading: false,
    loadingFacts: false,
    loadingIssues: false,
    error: '',
    success: '',
    phases: [],
    workbench: null,
    selectedImport: null,
    facts: [],
    metricCode: '',
    fiscalYear: '',
    manualRows: [],
    completionNotes: '',
    imports: [],
    importRowCount: 0,
    importPaginationModel: { page: 0, pageSize: 25 },
    factRowCount: 0,
    factPaginationModel: { page: 0, pageSize: 50 },
    issues: [],
    issueRowCount: 0,
    issuePaginationModel: { page: 0, pageSize: 25 },
    issueSeverity: '',

    gaps: [],
    gapSummary: {
      gapcount: 0,
      filteredcount: 0,
      yearcount: 0,
      metriccount: 0,
      estimatecount: 0,
      manualcount: 0,
      historicalyears: [],
    },
    gapRowCount: 0,
    gapPaginationModel: { page: 0, pageSize: 50 },
    selectedGapKeys: [],
    loadingGaps: false,
    downloadingGaps: false,

    gapResolutionMode: 'UNSET',
  };

  componentDidMount() {
    void this.initialize();
  }

  private getPageParam = (key: string): unknown => {
    const params: any = this.props.params ?? {};
    return params[key] ?? params?.get?.(key) ?? undefined;
  };

  private getPlanningCycleId = (): number => {
    //const value = this.getPageParam('planningcycleid');
    const value = this.props.plancontext?.pci;
    //console.log('biehis.pci',value);
    const result = Number(value);
    return Number.isFinite(result) && result > 0 ? result : 0;
  };

  private initialize = async () => {
    const planningcycleid = this.getPlanningCycleId();

    if (!planningcycleid) {
      this.setState({
        error: 'Planning Cycle context is missing. Open BIE Historical from the Planning Cycle workbench.',
      });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      const [phases] = await Promise.all([fetchPlanningJourneyPhases()]);
      this.setState({ phases });
      await this.getWorkbench();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private getWorkbench = async () => {
    const planningcycleid = this.getPlanningCycleId();
    if (!planningcycleid) return;

    const previousSelectedId = this.state.selectedImport?.rid ?? null;
    const workbench = await fetchHistoricalWorkbench(planningcycleid);
    const selectedImport = previousSelectedId
      ? workbench.imports.find((row) => row.rid === previousSelectedId) ?? null
      : null;

    const imports = workbench.imports;
    const importRowCount = workbench.imports?.length ?? 0;
    const importPaginationModel = { page: 0, pageSize: Math.max(5,importRowCount) };

    await new Promise<void>((resolve) => {
      this.setState({
        workbench,
        imports,
        importRowCount,
        importPaginationModel,
        selectedImport,
        facts: selectedImport ? this.state.facts : [],
        fiscalYear: this.state.fiscalYear || workbench.context.fiscalyear || '',
        selectedGapKeys: [],
      }, resolve);
    });

    // Imports are repository provenance, while issues and gaps are Planning Cycle readiness.
    await Promise.all([
      this.loadImportsPage(),
      this.loadIssuesPage(),
      ...(this.state.tab === 'coverage' ? [this.loadGapsPage()] : []),
    ]);

    if (selectedImport) {
      await this.loadFacts(selectedImport, { page: 0, pageSize: this.state.factPaginationModel.pageSize });
    }
  };

  private loadImportsPage = async (model: GridPaginationModel = this.state.importPaginationModel) => {
    const context = this.state.workbench?.context;
    if (!context) return;

    const result = await fetchHistoricalImportsPage({
      // Do not provenance-filter the institutional repository by the active Planning Cycle.
      planningcycleid: null,
      historicaldatasetid: null,
      page: model.page,
      pageSize: model.pageSize,
    });

    const selectedImport = this.state.selectedImport
      ? result.rows.find((row) => row.rid === this.state.selectedImport?.rid) ?? null
      : null;

    this.setState({
      imports: result.rows,
      importRowCount: result.rowCount,
      importPaginationModel: { page: result.page, pageSize: result.pageSize },
      selectedImport,
      facts: selectedImport ? this.state.facts : [],
      factRowCount: selectedImport ? this.state.factRowCount : 0,
    });
  };

  private loadIssuesPage = async (
    model: GridPaginationModel = this.state.issuePaginationModel,
    severity: HistoricalIssueSeverity | '' = this.state.issueSeverity,
  ) => {
    const context = this.state.workbench?.context;
    if (!context) return;

    this.setState({ loadingIssues: true });
    try {
      const result = await fetchHistoricalIssuesPage({
        planningcycleid: context.planningcycleid,
        historicaldatasetid: context.historicaldatasetid,
        severityCode: severity || null,
        resolved: 0,
        page: model.page,
        pageSize: model.pageSize,
      });
      this.setState({
        issues: result.rows,
        issueRowCount: result.rowCount,
        issuePaginationModel: { page: result.page, pageSize: result.pageSize },
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loadingIssues: false });
    }
  };

  private loadGapsPage = async (
    model: GridPaginationModel = this.state.gapPaginationModel,
  ) => {
    const context = this.state.workbench?.context;
    if (!context) return;

    this.setState({ loadingGaps: true });

    try {
      const result = await fetchHistoricalGapsPage({
        planningcycleid: context.planningcycleid,
        historicaldatasetid: context.historicaldatasetid,
        page: model.page,
        pageSize: model.pageSize,
      });

      this.setState({
        gaps: result.rows,
        gapSummary: result.summary,
        gapRowCount: result.rowCount,
        gapPaginationModel: {
          page: result.page,
          pageSize: result.pageSize,
        },
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loadingGaps: false });
    }
  };

  private changeGapPage = async (model: GridPaginationModel) => {
    this.setState({ gapPaginationModel: model });
    await this.loadGapsPage(model);
  };

  private changeSelectedGapKeys = (selectedGapKeys: string[]) => {
    this.setState({ selectedGapKeys });
  };

  private handleDownloadAllGaps = async (): Promise<void> => {
    const context = this.state.workbench?.context;
    if (!context) return;

    try {
      this.setState({ downloadingGaps: true });

      await downloadAllHistoricalGaps(
        context.planningcycleid,
        context.historicaldatasetid ?? null,
      );
    } catch (error) {
      if (this.props.showNotification)
      this.props.showNotification(
        error instanceof Error ? error.message : 'Gap download failed.',
        'error',
      );
    } finally {
      this.setState({ downloadingGaps: false });
    }
  };

  private handleDownloadSelectedGaps = async (): Promise<void> => {
    const context = this.state.workbench?.context;
    if (!context) return;

    try {
      this.setState({ downloadingGaps: true });

      await downloadSelectedHistoricalGaps(
        context.planningcycleid,
        this.state.selectedGapKeys,
        context.historicaldatasetid ?? null,
      );
    } catch (error) {
      if (this.props.showNotification)
      this.props.showNotification(
        error instanceof Error
          ? error.message
          : 'Selected-gap download failed.',
        'error',
      );
    } finally {
      this.setState({ downloadingGaps: false });
    }
  };

  private changeTab = async (tab: HistoricalTab) => {
    this.setState({ tab });

    if (tab === 'coverage') {
      await this.loadGapsPage({
        page: 0,
        pageSize: this.state.gapPaginationModel.pageSize,
      });
    }
  };

  private changeIssuePage = async (model: GridPaginationModel) => {
    this.setState({ issuePaginationModel: model });
    await this.loadIssuesPage(model);
  };

  private changeIssueSeverity = async (issueSeverity: HistoricalIssueSeverity | '') => {
    const model = { page: 0, pageSize: this.state.issuePaginationModel.pageSize };
    this.setState({ issueSeverity, issuePaginationModel: model });
    await this.loadIssuesPage(model, issueSeverity);
  };

  private loadFacts = async (
    selectedImport: HistoricalImportRow,
    model: GridPaginationModel = this.state.factPaginationModel,
  ) => {
    const context = this.state.workbench?.context;
    if (!context) return;

    this.setState({ loadingFacts: true, error: '' });

    try {
      const result = await fetchHistoricalFactsPage({
        // The selected import/dataset identifies provenance. The active Planning Cycle
        // must not exclude facts originally published during Onboarding.
        planningcycleid: null,
        historicaldatasetid: selectedImport.historicaldatasetid ?? null,
        importid: selectedImport.rid,
        trackid: selectedImport.trackid || null,
        page: model.page,
        pageSize: model.pageSize,
      });

      this.setState({
        facts: result.rows,
        factRowCount: result.rowCount,
        factPaginationModel: { page: result.page, pageSize: result.pageSize },
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loadingFacts: false });
    }
  };

  private changeImportPage = async (model: GridPaginationModel) => {
    this.setState({ importPaginationModel: model, selectedImport: null, facts: [], factRowCount: 0 });
    try {
      await this.loadImportsPage(model);
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    }
  };

  private changeFactPage = async (model: GridPaginationModel) => {
    const selectedImport = this.state.selectedImport;
    if (!selectedImport) return;
    this.setState({ factPaginationModel: model });
    await this.loadFacts(selectedImport, model);
  };

  private selectImport = async (selectedImport: HistoricalImportRow | null) => {
    const factPaginationModel = { page: 0, pageSize: this.state.factPaginationModel.pageSize };
    this.setState({ selectedImport, facts: [], factRowCount: 0, factPaginationModel });
    if (selectedImport) await this.loadFacts(selectedImport, factPaginationModel);
  };

  private generateMatrix = async () => {
    const context = this.state.workbench?.context;
    if (!context) return;

    if (this.state.manualRows.some((row) => row.__dirty)) {
      this.setState({
        error: 'Save or discard the current manual changes before generating another matrix.',
      });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      const rows = await generateHistoricalMatrix({
        planningcycleid: context.planningcycleid,
        historicaldatasetid: context.historicaldatasetid,
        metricCode: this.state.metricCode,
        fiscalYear: this.state.fiscalYear,
      });

      this.setState({
        manualRows: rows.map((row, index) => ({
          ...row,
          rid: row.rid || `MATRIX-${index + 1}`,
          __new: !row.matrixexisting,
          __dirty: false,
        })),
        success: `The Matrix analysed ${rows.length} expected observation(s) and identified ${rows.filter((row) => !row.matrixexisting).length} unresolved gap(s).`,
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private commitMatrix0 = async () => {
    const context = this.state.workbench?.context;
    if (!context) return;

    const changedRows = this.state.manualRows.filter(
      (row) => !row.matrixexisting && row.__dirty,
    );

    if (!changedRows.length) {
      this.setState({ success: 'There are no manual changes to save.' });
      return;
    }

    const invalidRows = changedRows.filter((row) => Boolean(row.__error));
    if (invalidRows.length) {
      this.setState({ error: `${invalidRows.length} manual row(s) require correction before saving.` });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      // await saveHistoricalFacts(
      //   context.planningcycleid,
      //   context.historicaldatasetid,
      //   changedRows.map((row) => ({
      //     rid: row.__new ? undefined : row.rid,
      //     rix: row.rix,
      //     stp: row.stamp,
      //     mtc: row.metriccode,
      //     prc: row.productcode,
      //     loc: row.locationcode || undefined,
      //     pec: row.periodcode,
      //     qty: Number(row.quantity || 0),
      //     upr: Number(row.unitprice || 0),
      //     amt: Number(row.amount || 0),
      //     rmk: row.remarks || '',
      //   })),
      // );
      const records: MatrixResolutionSaveRecord[] = this.state.manualRows
        .filter((row) => row.__dirty && !row.matrixexisting)
        .map((row) => ({
          src: row.rid,
          dri: Number((row.raw as any)?.dri),
          pei: Number((row.raw as any)?.pei),
          pri: (row.raw as any)?.pri ?? null,
          eni: (row.raw as any)?.eni ?? null,
          lci: (row.raw as any)?.lci ?? null,
          aci: (row.raw as any)?.aci ?? null,
          qty: Number(row.quantity || 0),
          upr: Number(row.unitprice || 0),
          amt: Number(row.amount || 0),
          ref: String((row.raw as any)?.ref || ''),
          rmk: String(row.remarks || ''),
          //rsc: String(row.resolutioncode || 'MANUAL') as MatrixResolutionCode,
          rsc: String('MANUAL') as MatrixResolutionCode,
        }));

      const result = await commitMatrixResolutions(
        this.props.params?.planningcycleid,
        this.props.params?.historicaldatasetid,
        records,
      );
      const {dataset,planningcycle,submittedcount,committedcount} = result;

      this.setState({
        manualRows: this.state.manualRows.map((row) => ({
          ...row,
          __new: false,
          __dirty: false,
        })),
        success: 'Matrix committed.',
      });

      await this.getWorkbench();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private commitMatrix = async () => {
    const context = this.state.workbench?.context;
    if (!context) return;

    const changedRows = this.state.manualRows.filter(
      (row) => !row.matrixexisting && row.__dirty,
    );

    if (!changedRows.length) {
      this.setState({
        error: '',
        success: 'There are no Matrix changes to commit.',
      });
      return;
    }

    const invalidRows = changedRows.filter((row) => Boolean(row.__error));

    if (invalidRows.length) {
      this.setState({
        error: `${invalidRows.length} Matrix row${invalidRows.length === 1 ? '' : 's'
          } require correction before committing.`,
        success: '',
      });
      return;
    }

    this.setState({
      loading: true,
      error: '',
      success: '',
    });

    try {
      const records: MatrixResolutionSaveRecord[] = changedRows.map((row) => ({
        src: row.rid,

        dri: Number((row.raw as any)?.dri),
        pei: Number((row.raw as any)?.pei),

        pri: (row.raw as any)?.pri ?? null,
        eni: (row.raw as any)?.eni ?? null,
        lci: (row.raw as any)?.lci ?? null,
        aci: (row.raw as any)?.aci ?? null,

        qty: Number(row.quantity || 0),
        upr: Number(row.unitprice || 0),
        amt: Number(row.amount || 0),

        ref: String((row.raw as any)?.ref || ''),
        rmk: String(row.remarks || ''),

        rsc: 'MANUAL',
      }));

      const result = await commitMatrixResolutions(
        context.planningcycleid,
        context.historicaldatasetid,
        records,
      );

      const committedcount = Number(result.committedcount || records.length);

      await new Promise<void>((resolve) => {
        this.setState(
          {
            manualRows: [],
            success: `${committedcount} Matrix observation${committedcount === 1 ? '' : 's'
              } committed successfully.`,
          },
          resolve,
        );
      });

      await this.getWorkbench();
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
    } finally {
      this.setState({
        loading: false,
      });
    }
  };

  private saveManualRows = async () => {
    const context = this.state.workbench?.context;
    if (!context) return;

    const changedRows = this.state.manualRows.filter(
      (row) => !row.matrixexisting && row.__dirty,
    );

    if (!changedRows.length) {
      this.setState({ success: 'There are no manual changes to save.' });
      return;
    }

    const invalidRows = changedRows.filter((row) => Boolean(row.__error));
    if (invalidRows.length) {
      this.setState({ error: `${invalidRows.length} manual row(s) require correction before saving.` });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      await saveHistoricalFacts(
        context.planningcycleid,
        context.historicaldatasetid,
        changedRows.map((row) => ({
          rid: row.__new ? undefined : row.rid,
          rix: row.rix,
          stp: row.stamp,
          mtc: row.metriccode,
          prc: row.productcode,
          loc: row.locationcode || undefined,
          pec: row.periodcode,
          qty: Number(row.quantity || 0),
          upr: Number(row.unitprice || 0),
          amt: Number(row.amount || 0),
          rmk: row.remarks || '',
        })),
      );

      this.setState({
        manualRows: this.state.manualRows.map((row) => ({
          ...row,
          __new: false,
          __dirty: false,
        })),
        success: 'Historical facts saved.',
      });

      await this.getWorkbench();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private useForecastingForGaps0 = () => {
    const workbench = this.state.workbench;
    if (!workbench) return;

    const setParam = this.props.onParam('set');
    setParam('historicalgapstrategy', 'FORECAST');
    setParam('historicalgapstrategylabel', 'Forecast eligible remaining gaps');

    this.setState({
      gapResolutionMode: 'FORECAST',
      success: 'Forecasting selected for eligible remaining historical gaps. Validate coverage before completion.',
      error: '',
    });
  };

  private useForecastingForGaps = async () => {
    const planningcycleid = this.state.workbench?.context.planningcycleid;
    if (!planningcycleid) return;

    this.setState({ loading: true, error: '', success: '' });

    try {
      await setHistoricalGapStrategy(
        planningcycleid,
        'FORECAST_ELIGIBLE',
      );

      const setParam = this.props.onParam('set');
      setParam('historicalgapstrategy', 'FORECAST_ELIGIBLE');
      setParam(
        'historicalgapstrategylabel',
        'Forecast eligible remaining gaps',
      );

      this.setState({
        gapResolutionMode: 'FORECAST',
        success:
          'Forecasting selected for eligible remaining historical gaps.',
      });

      await this.getWorkbench();
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  private validateCoverage = async () => {
    const planningcycleid = this.state.workbench?.context.planningcycleid;
    if (!planningcycleid) return;

    this.setState({ loading: true, error: '', success: '' });

    try {
      await validateHistoricalCoverage(planningcycleid);
      this.setState({ success: 'Historical coverage and integrity validated.' });
      await this.getWorkbench();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private completeHistorical = async () => {
    const context = this.state.workbench?.context;
    if (!context) return;

    this.setState({ loading: true, error: '', success: '' });

    try {
      const strategyNote = this.state.gapResolutionMode === 'FORECAST'
        ? 'Gap resolution strategy: Forecast eligible remaining gaps.'
        : '';
      const notes = [this.state.completionNotes.trim(), strategyNote]
        .filter(Boolean)
        .join('\n');

      await completeHistoricalPhase(
        context.planningcycleid,
        notes,
      );
      this.setState({ success: 'Historical baseline completed.' });
      await this.getWorkbench();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private exitToNextBiePhase = () => {
    const context = this.state.workbench?.context;
    if (!context) return;

    const setParam = this.props.onParam('set');
    setParam('planningcycleid', context.planningcycleid);
    if (context.planningcycleix) setParam('planningcycleix', context.planningcycleix);
    setParam('planningcyclephase', 'BIE_FORECASTING');
    setParam('biepath', 'FORECASTING');
    if (context.historicaldatasetid) {
      setParam('historicaldatasetid', context.historicaldatasetid);
    }
    if (context.historicaldatasetix) {
      setParam('historicaldatasetix', context.historicaldatasetix);
    }

    //console.log('');
    this.props.setPage(
      this.state.workbench?.actions.nextpage || 'bieforecasting',
    );
  };

  private renderContext = () => {
    const context = this.state.workbench?.context;
    if (!context) return null;

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="caption" color="text.secondary">Planning Cycle</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {context.planningcyclename || context.planningcyclecode}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {context.budgetperiodname || context.fiscalyear || 'Budget period not supplied'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="caption" color="text.secondary">Historical Dataset</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {context.historicaldatasetname || context.historicaldatasetcode || `${this.state.workbench?.summary.datasetcount ?? 0} repository dataset(s)`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[context.coveragestartdate, context.coverageenddate].filter(Boolean).join(' — ') || `${this.state.workbench?.summary.factcount ?? 0} historical fact(s) available`}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="caption" color="text.secondary">Workflow</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Chip size="small" label={context.phasename || context.phasecode} />
              {this.state.workbench?.readiness.complete && (
                <Chip size="small" color="success" label="Complete" />
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  private renderCoverage = () => {
    const workbench = this.state.workbench;
    if (!workbench) return null;

    return (
      <HistoricalIntegrityPanel
        workbench={workbench}
        gaps={this.state.gaps}
        gapSummary={this.state.gapSummary}
        gapRowCount={this.state.gapRowCount}
        gapPaginationModel={this.state.gapPaginationModel}
        selectedGapKeys={this.state.selectedGapKeys}
        loadingGaps={this.state.loadingGaps}
        downloadingGaps={this.state.downloadingGaps}
        onGapPaginationModelChange={this.changeGapPage}
        onSelectedGapKeysChange={this.changeSelectedGapKeys}
        onDownloadAllGaps={() => void this.handleDownloadAllGaps()}
        onDownloadSelectedGaps={() => void this.handleDownloadSelectedGaps()}
        issues={this.state.issues}
        issueRowCount={this.state.issueRowCount}
        issuePaginationModel={this.state.issuePaginationModel}
        issueSeverity={this.state.issueSeverity}
        loading={this.state.loadingIssues || this.state.loading}
        onIssuePaginationModelChange={this.changeIssuePage}
        onSeverityChange={this.changeIssueSeverity}
        onValidate={() => void this.validateCoverage()}
      />
    );
  };

  private renderComplete = () => {
    const workbench = this.state.workbench;
    if (!workbench) return null;

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <div>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Complete Historical Baseline</Typography>
            <Typography variant="body2" color="text.secondary">
              Completion certifies the validated Planning Cycle historical baseline and enables the governed Forecasting workbench.
            </Typography>
          </div>

          {!workbench.readiness.complete && workbench.readiness.blockingcount > 0 && (
            <Alert severity="error">
              Completion is blocked by {workbench.readiness.blockingcount} unresolved integrity issue(s).
              Resolve the issues and rerun validation.
            </Alert>
          )}

          {!workbench.readiness.complete && !workbench.validation.validatedat && (
            <Alert severity="info">Run Coverage & Integrity validation before completion.</Alert>
          )}

          <TextField
            size="small"
            label="Completion Notes"
            value={this.state.completionNotes}
            onChange={(event) => this.setState({ completionNotes: event.target.value })}
            multiline
            minRows={3}
            fullWidth
            disabled={workbench.readiness.complete}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            {!workbench.readiness.complete ? (
              <Button
                variant="contained"
                onClick={() => void this.completeHistorical()}
                disabled={!workbench.actions.cancomplete || this.state.loading}
              >
                Complete Historical Baseline
              </Button>
            ) : (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={this.exitToNextBiePhase}
                disabled={!workbench.actions.canexit}
              >
                Continue to BIE Forecasting
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  };

  private renderTab = () => {
    const workbench = this.state.workbench;
    if (!workbench) return null;

    switch (this.state.tab) {
      case 'overview':
        return (
          <HistoricalOverviewPanel
            workbench={workbench}
            gapResolutionMode={this.state.gapResolutionMode}
            onOpenImports={() => this.setState({ tab: 'imports' })}
            onOpenWorkbook={() => this.setState({ tab: 'workbook' })}
            onOpenManualEntry={() => this.setState({ tab: 'manual' })}
            onOpenCoverage={() => void this.changeTab('coverage')}
            onUseForecasting={this.useForecastingForGaps}
          />
        );

      case 'manual':
        return (
          <HistoricalManualEntryGrid
            rows={this.state.manualRows}
            loading={this.state.loading}
            metricCode={this.state.metricCode}
            fiscalYear={this.state.fiscalYear}
            onMetricChange={(metricCode) => this.setState({ metricCode })}
            onFiscalYearChange={(fiscalYear) => this.setState({ fiscalYear })}
            onGenerate={this.generateMatrix}
            onRowsChange={(manualRows) => this.setState({ manualRows })}
            //onSave={this.saveManualRows}
            onSave={this.commitMatrix}
            allowedFiscalYears={workbench.context.allowedhistoricalyears}
            canEdit={workbench.actions.canmanualentry}
          />
        );

      case 'workbook':
        return (
          <HistoricalWorkbookPanel
            planningcycleid={workbench.context.planningcycleid}
            historicaldatasetid={workbench.context.historicaldatasetid}
            //metricCode={this.state.metricCode}
            //fiscalYear={this.state.fiscalYear}
            onCompleted={async () => {
              this.setState({ success: 'Workbook import completed.' });
              await this.getWorkbench();
            }}
            showNotification={this.props.showNotification}
          />
        );

      case 'coverage':
        return this.renderCoverage();

      case 'complete':
        return this.renderComplete();

      default:
        return (
          <HistoricalImportMasterDetail
            imports={this.state.imports}
            importRowCount={this.state.importRowCount}
            importPaginationModel={this.state.importPaginationModel}
            onImportPaginationModelChange={this.changeImportPage}
            facts={this.state.facts}
            factRowCount={this.state.factRowCount}
            factPaginationModel={this.state.factPaginationModel}
            onFactPaginationModelChange={this.changeFactPage}
            loadingImports={this.state.loading}
            loadingFacts={this.state.loadingFacts}
            selectedImport={this.state.selectedImport}
            onSelectImport={this.selectImport}
          />
        );
    }
  };

  render() {
    const workbench = this.state.workbench;

    return (
      <PlanningWorkbenchShell
        {...this.props}
        onBack={this.props.onBack}

        title="BIE Historical Baseline"
        subtitle="Review, supplement and certify historical facts for the selected Planning Cycle."
        icon={<HistoryIcon />}
        loading={this.state.loading}
        error={this.state.error}
        success={this.state.success}
        onRefresh={this.getWorkbench}
        headerExtension={
          <FPlanningJourney
            phases={this.state.phases}
            currentPhaseCode={workbench?.context.phasecode || 'HISTORICAL_DATA'}
          />
        }
      >
        <Stack spacing={2}>
          {this.renderContext()}

          {!workbench && !this.state.loading && !this.state.error && (
            <Alert severity="info">Loading Historical workbench context.</Alert>
          )}

          {workbench && (
            <>
              <Paper elevation={1}>
                <Tabs
                  value={this.state.tab}
                  onChange={(_, tab: HistoricalTab) => void this.changeTab(tab)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab value="overview" label="Overview" />
                  <Tab value="imports" label="Imports" />
                  <Tab value="workbook" label="Workbook" disabled={!workbench.actions.canimport} />
                  <Tab value="manual" label="Manual Entry" disabled={!workbench.actions.canmanualentry} />
                  <Tab value="coverage" label="Coverage & Integrity" />
                  <Tab value="complete" label="Complete" />
                </Tabs>
              </Paper>

              <Box>{this.renderTab()}</Box>
            </>
          )}
        </Stack>
      </PlanningWorkbenchShell>
    );
  }
}

export default BieHistoricalWorkbench;
